import React, { useState, useRef, useEffect } from 'react';
import { X, PanelRightClose, Columns2, Maximize2, Send, FolderOpen, ChevronDown, Lock, Sparkles, ArrowLeft, Bookmark, Copy, ThumbsUp, ThumbsDown, Check, Wrench, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AnchorIcon } from '@/components/ui/anchor-icon';
import { useChat } from '@/context/ChatContext';
import { toast } from 'sonner';

const INITIAL_SUGGESTIONS = [
  "What are the key goals in this project?",
  "Show me recent updates",
  "What should I focus on next?"
];

const TOOLS = [
  { id: 'slack', name: 'Slack', icon: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
    </svg>
  )},
  { id: 'github', name: 'GitHub', icon: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
    </svg>
  )},
  { id: 'figma', name: 'Figma', icon: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 24c2.208 0 4-1.792 4-4v-4H8c-2.208 0-4 1.792-4 4s1.792 4 4 4z"/>
      <path d="M4 12c0-2.208 1.792-4 4-4h4v8H8c-2.208 0-4-1.792-4-4z"/>
      <path d="M4 4c0-2.208 1.792-4 4-4h4v8H8C5.792 8 4 6.208 4 4z"/>
      <path d="M12 0h4c2.208 0 4 1.792 4 4s-1.792 4-4 4h-4V0z"/>
      <path d="M20 12c0 2.208-1.792 4-4 4s-4-1.792-4-4 1.792-4 4-4 4 1.792 4 4z"/>
    </svg>
  )},
  { id: 'notion', name: 'Notion', icon: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.139c-.093-.514.28-.887.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.667c0-.839.374-1.54 1.447-1.632z"/>
    </svg>
  )},
  { id: 'linear', name: 'Linear', icon: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M0 0h24v24H0V0zm2 2v20h20V2H2zm2 18V4h16v16H4zm2-2h12V6H6v10zm2-8v6h8V8H8z"/>
    </svg>
  )},
  { id: 'jira', name: 'Jira', icon: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24.013 12.5V1.005A1.001 1.001 0 0 0 23.013 0z"/>
    </svg>
  )}
];

export function AskAnchorPanel() {
  const chat = useChat();
  const { messages, activeConversation, panelOpen, layout, loading } = chat;

  const [input, setInput] = useState('');
  const [width, setWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  const [showLayoutMenu, setShowLayoutMenu] = useState(false);
  const [showToolMenu, setShowToolMenu] = useState(false);
  const [selectedTools, setSelectedTools] = useState([]);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  const scrollRef = useRef(null);
  const layoutMenuRef = useRef(null);
  const toolMenuRef = useRef(null);
  const titleInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (layoutMenuRef.current && !layoutMenuRef.current.contains(e.target)) {
        setShowLayoutMenu(false);
      }
      if (toolMenuRef.current && !toolMenuRef.current.contains(e.target)) {
        setShowToolMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseDown = (e) => {
    if (layout !== 'panel') return;
    setIsResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || layout !== 'panel') return;
      const newWidth = window.innerWidth - e.clientX;
      setWidth(Math.min(Math.max(newWidth, 320), 800));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, layout]);

  const handleSend = () => {
    if (!input.trim() || loading) return;

    const value = input;
    setInput('');

    if (activeConversation?.id) {
      // Send message to existing conversation
      chat.sendMessage(value, selectedTools.length > 0 ? selectedTools : null);
    } else {
      // Start a new conversation with this message
      chat.startNewChat(value, activeConversation?.project_id || null);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
  };

  const toggleTool = (toolId) => {
    setSelectedTools(prev =>
      prev.includes(toolId)
        ? prev.filter(id => id !== toolId)
        : [...prev, toolId]
    );
  };

  const handleTitleSave = () => {
    if (editTitle.trim() && editTitle.trim() !== activeConversation?.title) {
      chat.updateConversation({ title: editTitle.trim() });
    }
    setIsEditingTitle(false);
  };

  const handleTitleEdit = () => {
    setEditTitle(activeConversation?.title || '');
    setIsEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 50);
  };

  const handleCopyMessage = async (messageContent, messageId) => {
    try {
      await navigator.clipboard.writeText(messageContent);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleToggleSaved = () => {
    if (activeConversation) {
      chat.updateConversation({ is_saved: !activeConversation.is_saved });
    }
  };

  if (!panelOpen) return null;

  const isFlexChild = layout === 'split' || layout === 'fullscreen';
  const containerWidth = layout === 'panel' ? `${width}px` : layout === 'split' ? '50%' : '100%';

  const layoutOptions = [
    { value: 'panel', icon: PanelRightClose, label: 'Side Panel' },
    { value: 'split', icon: Columns2, label: 'Split View' },
    { value: 'fullscreen', icon: Maximize2, label: 'Full Screen' }
  ];

  const projectName = activeConversation?.project_name;

  // Render a message (handles both old format and new API format)
  const renderMessage = (message) => {
    const isUser = message.role === 'user';
    const isAssistant = message.role === 'assistant';

    return (
      <div
        key={message.id}
        className={cn(
          "flex gap-3",
          isUser ? "justify-end" : "justify-start"
        )}
      >
        {isAssistant && (
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
            <AnchorIcon className="w-4 h-4 text-primary" />
          </div>
        )}

        <div className={cn(
          "max-w-[85%] space-y-3",
          isUser && "flex flex-col items-end"
        )}>
          {isUser ? (
            <div className={cn(
              "px-4 py-3 bg-primary text-primary-foreground rounded-2xl rounded-tr-md",
              message.error && "opacity-70 border-2 border-red-500"
            )}>
              {message.content}
              {message.error && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-red-200">Failed to send</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      chat.retryMessage(message.id);
                    }}
                    className="text-xs text-red-200 hover:text-white underline flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Retry
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {message.structured ? (
                <>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm">{message.structured.headline}</h4>
                    <ul className="space-y-1.5">
                      {message.structured.points?.map((point, idx) => (
                        <li key={idx} className="text-sm text-neutral-700 dark:text-neutral-300 flex gap-2">
                          <span className="text-primary mt-1">&bull;</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {message.structured.insight && (
                    <div className="px-3 py-2.5 rounded-lg bg-primary/10 border border-primary/20">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-neutral-700 dark:text-neutral-300">
                          {message.structured.insight}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">
                    {message.content}
                  </p>
                </div>
              )}

              {/* Message actions (fullscreen only) */}
              {layout === 'fullscreen' && isAssistant && (
                <div className="flex items-center gap-1 mt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                    onClick={() => handleCopyMessage(message.content, message.id)}
                  >
                    {copiedMessageId === message.id ? (
                      <Check className="w-3.5 h-3.5 text-green-500" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                  >
                    <ThumbsUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                  >
                    <ThumbsDown className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col",
        "bg-white/80 dark:bg-neutral-900/80 backdrop-blur-xl",
        "border-l-2 border-primary/20",
        "shadow-2xl",
        isFlexChild
          ? "relative h-full"
          : "fixed top-0 right-0 h-screen z-50 animate-in slide-in-from-right duration-300"
      )}
      style={{ width: isFlexChild ? undefined : containerWidth, flex: isFlexChild ? (layout === 'split' ? '0 0 50%' : '1') : undefined }}
    >
      {layout === 'panel' && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 transition-colors"
          onMouseDown={handleMouseDown}
        />
      )}

      {/* Header - Fullscreen mode */}
      {layout === 'fullscreen' ? (
        <div className="sticky top-0 z-10 border-b border-neutral-200 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-lg">
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 flex-shrink-0"
                onClick={() => chat.setLayout('panel')}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>

              {isEditingTitle ? (
                <input
                  ref={titleInputRef}
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onBlur={handleTitleSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTitleSave();
                    if (e.key === 'Escape') setIsEditingTitle(false);
                  }}
                  className="flex-1 font-semibold text-lg bg-transparent border-b-2 border-primary outline-none min-w-0"
                />
              ) : (
                <h2
                  className="font-semibold text-lg truncate cursor-pointer hover:text-primary transition-colors"
                  onClick={handleTitleEdit}
                  title="Click to edit title"
                >
                  {activeConversation?.title || 'Ask Anchor'}
                </h2>
              )}

              {projectName && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  {projectName}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400 px-2 py-1 rounded-md bg-neutral-100 dark:bg-neutral-800">
                <Lock className="w-3 h-3" />
                Private to you
              </div>

              <Button
                variant="ghost"
                size="sm"
                className={cn("h-8 w-8 p-0", activeConversation?.is_saved && "text-yellow-500")}
                onClick={handleToggleSaved}
                title={activeConversation?.is_saved ? 'Remove bookmark' : 'Bookmark'}
              >
                <Bookmark className={cn("w-4 h-4", activeConversation?.is_saved && "fill-current")} />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={chat.closePanel}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* Header - Panel/Split mode */
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-2">
            <AnchorIcon className="w-5 h-5 text-primary" />
            <h2 className="font-semibold text-lg">
              {activeConversation?.title || 'Ask Anchor'}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400 px-2 py-1 rounded-md bg-neutral-100 dark:bg-neutral-800">
              <Lock className="w-3 h-3" />
              Private to you
            </div>

            <div className="relative" ref={layoutMenuRef}>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setShowLayoutMenu(!showLayoutMenu)}
              >
                {layout === 'panel' && <PanelRightClose className="w-4 h-4" />}
                {layout === 'split' && <Columns2 className="w-4 h-4" />}
              </Button>

              {showLayoutMenu && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-1 z-50">
                  {layoutOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.value}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700",
                          layout === option.value && "bg-neutral-100 dark:bg-neutral-700 text-primary"
                        )}
                        onClick={() => {
                          chat.setLayout(option.value);
                          setShowLayoutMenu(false);
                        }}
                      >
                        <Icon className="w-4 h-4" />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={chat.closePanel}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Thread area */}
      <div ref={scrollRef} className={cn("flex-1 overflow-y-auto py-6", layout === 'fullscreen' ? 'px-6' : 'px-4')}>
       <div className={cn("space-y-6", layout === 'fullscreen' && "max-w-5xl mx-auto")}>
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full space-y-6">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <AnchorIcon className="w-8 h-8 text-primary" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold text-lg">How can I help?</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Ask me anything about {projectName || 'your project'}
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full max-w-sm">
              {INITIAL_SUGGESTIONS.map((suggestion, idx) => (
                <button
                  key={idx}
                  className="px-4 py-3 text-sm text-left rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-primary hover:bg-primary/5 transition-colors"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map(renderMessage)}

        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <AnchorIcon className="w-4 h-4 text-primary" />
            </div>
            <div className="flex items-center gap-1 px-4 py-3">
              <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
       </div>
      </div>

      {/* Input area */}
      <div className={cn("border-t border-neutral-200 dark:border-neutral-800 p-4 space-y-3", layout === 'fullscreen' && "bg-white/90 dark:bg-neutral-900/90 backdrop-blur-lg")}>
        <div className={cn(layout === 'fullscreen' && "max-w-5xl mx-auto space-y-3")}>
          <div className="flex items-end gap-2">
            <div className="flex-1 relative">
              <textarea
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, layout === 'fullscreen' ? 200 : 120) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Ask anything..."
                className={cn(
                  "w-full px-4 py-3 pr-12 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm",
                  layout === 'fullscreen' && "rounded-xl text-base py-4"
                )}
                rows={1}
                style={{ minHeight: layout === 'fullscreen' ? '52px' : '44px', maxHeight: layout === 'fullscreen' ? '200px' : '120px' }}
              />
            </div>
            <Button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              size="sm"
              className={cn("p-0 flex-shrink-0", layout === 'fullscreen' ? "h-[52px] w-[52px] rounded-xl" : "h-11 w-11")}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>

          {/* Filter pills */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Tools pill */}
            <div className="relative" ref={toolMenuRef}>
              <button
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                  selectedTools.length > 0
                    ? "bg-primary/10 text-primary hover:bg-primary/20"
                    : "bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700"
                )}
                onClick={() => setShowToolMenu(!showToolMenu)}
              >
                <Wrench className="w-3.5 h-3.5" />
                <span>Tools</span>
                {selectedTools.length > 0 && (
                  <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-primary/20 text-[10px]">
                    {selectedTools.length}
                  </span>
                )}
              </button>

              {showToolMenu && (
                <div className="absolute left-0 bottom-full mb-1 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-1 z-50">
                  {TOOLS.map((tool) => (
                    <button
                      key={tool.id}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700",
                        selectedTools.includes(tool.id) && "bg-primary/10 text-primary"
                      )}
                      onClick={() => toggleTool(tool.id)}
                    >
                      {tool.icon}
                      {tool.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Project pill */}
            {projectName && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-xs font-medium text-primary">
                <FolderOpen className="w-3.5 h-3.5" />
                <span>{projectName}</span>
              </div>
            )}

            {/* Selected tools as removable pills */}
            {selectedTools.map(toolId => {
              const tool = TOOLS.find(t => t.id === toolId);
              return (
                <div
                  key={toolId}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-primary/10 text-xs text-primary cursor-pointer hover:bg-primary/20"
                  onClick={() => toggleTool(toolId)}
                >
                  {tool.icon}
                  <span>{tool.name}</span>
                  <X className="w-3 h-3 ml-0.5" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
